import { Box, Collapse, Theme, Toolbar, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag'
import { useDeviceType } from '../../../rsmCoreComponents/hooks/useDeviceType'
import { hidePopover, setPopoverContent } from '../../../store/form/formSlice'
import { Styles } from '../../../types'
import { useAppDispatch, useAppSelector } from '../../../utils/hooks'
import { MyRsmLogo, NewRsmLogo } from '../../media'
import RsmDialog from '../../overlay/RsmDialog'
import { popoverComponentFactory } from '../../Profile/ProfileModal/ProfileModalContainer'
import DesktopMenu from '../DesktopMenu'
import HamburgerMenu from '../HamburgerMenu'
import SkipToMainContent from '../SkipToMainContent'
import WelcomeBar from '../WelcomeBar'
import tokens from '../../../styles/tokens.json'
import RouterNavListItem from '../../Navigation/NavListItem/RouterNavListItem'

const styles: Styles = {
  toolbar: () => ({
    display: 'flex',
    height: '5.4375rem',
    alignContent: 'center',
    alignItems: 'center',
  }),
  toolbarRedesign: (theme) => ({
    display: 'flex',
    height: '5.4375rem',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',

    [theme.breakpoints.down('tablet')]: {
      justifyContent: 'flex-end',
    },
    [theme.breakpoints.down('desktop')]: {
      justifyContent: 'flex-end',
    },
  }),
  mobileDialogModal: () => ({
    '& .MuiDialog-paper': {
      maxWidth: '20rem',
    },
  }),
  link: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    fontFamily: tokens.type.fontFamilies.preloBook,
    fontSize: '1rem',
    textDecoration: 'none',
    color: tokens.colors.white,
    whiteSpace: 'nowrap',
    paddingTop: '0.5rem',
    '&[aria-current="true"]': {
      fontFamily: tokens.type.fontFamilies.preloBold,
    },
    [theme.breakpoints.only('mobile')]: {
      paddingLeft: '0.75rem',
    },
  }),
  homeIcon: (theme) => ({
    position: 'absolute',
    left: 0,
    paddingBottom: '0.5rem',

    [theme.breakpoints.only('mobile')]: {
      paddingLeft: '0.5rem',
    },

    [theme.breakpoints.down('tablet')]: {
      '& svg': {
        width: '5rem',
      },
    },

    [theme.breakpoints.up('tablet')]: {
      top: '1.5rem',
    },
  }),
  welcomePrimary: {
    fontFamily: tokens.type.fontFamilies.preloBold,
    fontSize: '2.625rem',
    fontWeight: 700,
    letterSpacing: 0,
  },
  welcomeSecondary: {
    marginTop: '-0.8125rem',
    paddingLeft: '0.5rem',
    fontSize: '1rem',
    fontWeight: 500,
    fontFamily: tokens.type.fontFamilies.preloBold,
    lineHeight: '1.4375rem',
  },
}

type CombinedMenusProps = {
  currentItem?: string
  bottomBorder?: boolean
}

const CombinedMenus = ({
  currentItem = '',
  bottomBorder = true,
}: CombinedMenusProps) => {
  const { isMobile, isTablet } = useDeviceType()
  const {
    popover: { messageType },
  } = useAppSelector((state) => state)

  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const isUIRedesignEnabled = useFeatureFlag('UI_Redesign')
  const [open, setOpen] = useState<boolean>(messageType !== undefined)
  const [showWelcome, setShowWelcome] = useState<boolean>(true)

  const handleFocus = (event: React.FocusEvent<HTMLAnchorElement>) => {
    const target = event.currentTarget
    target.style.outline = `0.125rem solid ${tokens.colors.white}`
    target.style.outlineOffset = `0.25rem`
  }

  const handleBlur = (event: React.FocusEvent<HTMLAnchorElement>) => {
    const target = event.currentTarget
    target.style.outline = 'none'
  }

  const computedStyles = {
    toolbarContainer: useCallback(
      (theme: Theme) => ({
        height: 87,
        paddingLeft: '1rem',
        paddingRight: '1rem',
        color: tokens.colors.rsmGray.copy,
        borderBottomWidth: bottomBorder ? 2 : 0,
        borderBottomColor: tokens.colors.backgroundGray,
        [theme.breakpoints.only('tablet')]: {
          paddingLeft: '2rem',
          paddingRight: '2rem',
        },
        [theme.breakpoints.up('desktop')]: {
          paddingLeft: '6.5rem',
          paddingRight: '6.5rem',
        },
      }),
      [bottomBorder],
    ),
    toolbarContainerRedesign: useCallback(
      (theme: Theme) => ({
        height: 'fit-content',
        backgroundColor: tokens.colors.black,
        backgroundImage: `url(${process.env.PUBLIC_URL}/myRSMHeaderBackground.svg)`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        [theme.breakpoints.only('tablet')]: {
          paddingLeft: '2rem',
          paddingRight: '2rem',
        },
        [theme.breakpoints.up('desktop')]: {
          paddingLeft: '6.5rem',
          paddingRight: '6.5rem',
        },
      }),
      [],
    ),
  }

  const handleClose = () => {
    setOpen(false)
    dispatch(setPopoverContent(undefined))
  }

  useEffect(() => {
    if (isMobile) {
      const notify = messageType !== undefined && messageType !== null
      setOpen(notify)
      dispatch(hidePopover())
    } else {
      setOpen(false)
    }
  }, [messageType, isMobile])

  useEffect(() => {
    const handleScroll = () => {
      if (currentItem === 'home') {
        setShowWelcome(window.scrollY === 0)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div>
      <SkipToMainContent />
      <WelcomeBar />
      {isUIRedesignEnabled ? (
        <Box>
          <Box sx={computedStyles.toolbarContainerRedesign}>
            <Toolbar disableGutters sx={styles.toolbarRedesign}>
              <Box sx={styles.homeIcon}>
                <RouterNavListItem
                  to="/"
                  value="home"
                  testId="Lnk_Navigation_Home"
                  tabIndex={0}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  ariaLabel="My RSM home"
                  linkStyle={styles.link}>
                  {isUIRedesignEnabled ? (
                    <NewRsmLogo titleDescription="myRSM home" />
                  ) : (
                    <MyRsmLogo />
                  )}
                </RouterNavListItem>
              </Box>
              <Box sx={{ paddingLeft: '1rem' }}>
                {' '}
                {isMobile || isTablet ? (
                  <HamburgerMenu currentItem={currentItem} />
                ) : (
                  <DesktopMenu
                    currentItem={currentItem}
                    showWelcome={showWelcome}
                  />
                )}
              </Box>
            </Toolbar>
            <Collapse in={showWelcome} timeout={400}>
              {!isMobile && !isTablet && (
                <Box
                  sx={{
                    height: '8rem',
                    display: currentItem === 'home' ? '' : 'none',
                    alignContent: 'center',
                  }}>
                  <Typography
                    sx={styles.welcomePrimary}
                    data-testid="welcome-message">
                    {t('Navigation.Welcome')}.
                  </Typography>
                  <Typography
                    sx={styles.welcomeSecondary}
                    color="secondary"
                    data-testid="welcome-submessage">
                    {t('Navigation.WelcomeSubMessage')}
                  </Typography>
                </Box>
              )}
            </Collapse>
          </Box>
        </Box>
      ) : (
        <Box sx={computedStyles.toolbarContainer}>
          <Toolbar disableGutters sx={styles.toolbar}>
            <Box flexGrow={1}>
              <RouterNavListItem
                to="/"
                value="home"
                testId="Lnk_Navigation_Home"
                tabIndex={0}
                onFocus={handleFocus}
                onBlur={handleBlur}
                ariaLabel="My RSM home">
                <MyRsmLogo />
              </RouterNavListItem>
            </Box>

            {isMobile || isTablet ? (
              <HamburgerMenu currentItem={currentItem} />
            ) : (
              <DesktopMenu
                currentItem={currentItem}
                showWelcome={showWelcome}
              />
            )}
          </Toolbar>
        </Box>
      )}
      {isMobile && (
        <RsmDialog
          isOpen={open}
          closeHandler={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          dialogCustomProps={{
            sx: styles.mobileDialogModal,
          }}>
          <Box sx={{ p: 1 }}>{popoverComponentFactory(messageType)}</Box>
        </RsmDialog>
      )}
    </div>
  )
}

export default CombinedMenus
